/**
 * Device hash for the admin login device limit.
 *
 * bella-admin-api gates login on `ad_admin_unique_hash`: the hash we send is
 * looked up for this admin, and if it misses, a new row is inserted — unless
 * the admin already has `max_device_count` rows, in which case login is
 * refused with 403.
 *
 * Two things about this that are easy to get wrong:
 *
 * 1. It is NOT a fingerprint. Legacy mints
 *    `MD5(navigator.userAgent + user + uuidv4())` once and keeps it in
 *    localStorage forever (legacy/carselona_newadmin Authorization.ts). The
 *    random UUID means the value cannot be recomputed — it is just a random
 *    per-browser-profile token. So we read before we mint, and we use the SAME
 *    localStorage key and format as legacy. The day this panel is served from
 *    the legacy origin, it adopts the existing hash instead of burning a second
 *    device slot.
 *
 * 2. Sending nothing is worse than sending anything. The API's lookup helper
 *    short-circuits on an empty hash without querying, so a tokenless login can
 *    never match, inserts a junk empty row, and locks the account out at 403
 *    from then on. We always send a value.
 *
 * Not a secret and not a security control — it identifies a browser profile,
 * nothing more. localStorage is the right place for it.
 */
const DEVICE_HASH_KEY = "webKit_hash_id";

function mint(): string {
  // Two UUIDs stripped of dashes give 64 hex chars; the first 32 match
  // legacy's MD5 shape. crypto.randomUUID is available in every browser this
  // panel supports and is a better entropy source than the legacy chain.
  const raw = `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, "");
  return raw.slice(0, 32).toLowerCase();
}

/**
 * Returns this browser's device hash, minting and persisting one on first use.
 * Always returns a non-empty 32-char hex string, even when storage is blocked
 * (private mode) — in that case the value is per-page-load, which costs a
 * device slot but never sends an empty hash.
 */
export function getDeviceHash(): string {
  try {
    // Adopt ANY existing value rather than validating its shape. A stored hash
    // is whatever the server already has a row for; rewriting it because it
    // looks unfamiliar would burn a device slot, which is the exact failure
    // this function exists to avoid.
    const existing = localStorage.getItem(DEVICE_HASH_KEY);
    if (existing !== null && existing.trim() !== "") return existing;

    const minted = mint();
    localStorage.setItem(DEVICE_HASH_KEY, minted);
    return minted;
  } catch {
    return mint();
  }
}
